/**
 * HR API — timesheet codes, entries, lock, calendars
 * @see services/hrApi.js barrel export
 */

import { api } from '../../api';

// =============================================================================
// TIMESHEET CODES
// =============================================================================

export const getTimesheetCodes = async (projectId, includeGlobal = true) => {
  const params = new URLSearchParams();
  if (projectId) params.append('project_id', projectId);
  params.append('include_global', includeGlobal);
  const response = await api.get(`/timesheet/codes?${params.toString()}`);
  return response.data;
};

export const getTimesheetCode = async (codeId) => {
  const response = await api.get(`/timesheet/codes/${codeId}`);
  return response.data;
};

export const createTimesheetCode = async (codeData) => {
  const response = await api.post('/timesheet/codes', codeData);
  return response.data;
};

export const updateTimesheetCode = async (codeId, codeData) => {
  const response = await api.put(`/timesheet/codes/${codeId}`, codeData);
  return response.data;
};

export const deleteTimesheetCode = async (codeId) => {
  const response = await api.delete(`/timesheet/codes/${codeId}`);
  return response.data;
};

export const initDefaultCodes = async (projectId) => {
  const response = await api.post(`/timesheet/codes/init/${projectId}`);
  return response.data;
};

/**
 * Initialize global timesheet codes (for company-level personnel)
 * These codes have project_id = null and are used by Company Timesheet
 */
export const initGlobalTimesheetCodes = async () => {
  const response = await api.post('/timesheet/codes/init-global');
  return response.data;
};

/**
 * Check if global timesheet codes exist
 * Returns status and list of existing codes
 */
export const ensureGlobalTimesheetCodes = async () => {
  const response = await api.get('/timesheet/codes/ensure-global');
  return response.data;
};

// =============================================================================
// TIMESHEET ENTRIES
// =============================================================================

export const getTimesheetEntries = async (projectId, params = {}) => {
  const queryParams = new URLSearchParams();
  queryParams.append('project_id', projectId);
  if (params.personnel_id) queryParams.append('personnel_id', params.personnel_id);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  const response = await api.get(`/timesheet/entries?${queryParams.toString()}`);
  return response.data;
};

export const getTimesheetEntriesByMonth = async (projectId, year, month) => {
  const response = await api.get(`/timesheet/entries/by-month?project_id=${projectId}&year=${year}&month=${month}`);
  return response.data;
};

export const createTimesheetEntry = async (entryData) => {
  const response = await api.post('/timesheet/entries', entryData);
  return response.data;
};

export const updateTimesheetEntry = async (entryId, entryData) => {
  const response = await api.put(`/timesheet/entries/${entryId}`, entryData);
  return response.data;
};

export const deleteTimesheetEntry = async (entryId) => {
  const response = await api.delete(`/timesheet/entries/${entryId}`);
  return response.data;
};

// =============================================================================
// TIMESHEET PERIOD LOCK (Phase 3 - Lock Workflow)
// =============================================================================

/**
 * Lock a timesheet period for payroll processing
 * 
 * Validates timesheet entries exist, calculates aggregated totals,
 * and creates/updates the timesheet_period record with LOCKED status.
 * 
 * @param {Object} data - Lock request data
 * @param {number} data.personnel_id - Personnel ID
 * @param {number} data.year - Year (e.g., 2026)
 * @param {number} data.month - Month (1-12)
 * @returns {Promise<Object>} TimesheetPeriod response with totals and status
 */
export const lockTimesheetPeriod = async (data) => {
  try {
    const response = await api.post('/timesheet/lock', data);
    return response.data;
  } catch (error) {
    console.error('Error locking timesheet:', error);
    throw error;
  }
};

/**
 * Unlock a locked timesheet period
 * 
 * Requires a reason for audit trail. Cannot unlock PROCESSED periods
 * (those with a generated payslip).
 * 
 * @param {Object} data - Unlock request data
 * @param {number} data.personnel_id - Personnel ID
 * @param {number} data.year - Year (e.g., 2026)
 * @param {number} data.month - Month (1-12)
 * @param {string} data.reason - Reason for unlocking (min 10 characters)
 * @returns {Promise<Object>} TimesheetPeriod response with updated status
 */
export const unlockTimesheetPeriod = async (data) => {
  try {
    const response = await api.post('/timesheet/unlock', data);
    return response.data;
  } catch (error) {
    console.error('Error unlocking timesheet:', error);
    throw error;
  }
};

/**
 * Get timesheet period status and permissions
 * 
 * Returns current status, permissions (can_edit, can_lock, etc.),
 * and the full period record if it exists.
 * 
 * @param {number} personnelId - Personnel ID
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} Status response with permissions
 */
export const getTimesheetStatus = async (personnelId, year, month) => {
  try {
    const response = await api.get('/timesheet/status', {
      params: { personnel_id: personnelId, year, month }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching timesheet status:', error);
    throw error;
  }
};

/**
 * Get timesheet period status for multiple personnel at once (batch)
 * 
 * Optimized for loading Company Employees timesheet view.
 * Single API call instead of N calls.
 * 
 * @param {Array<number>} personnelIds - Array of Personnel IDs
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} Map of personnelId -> status info
 */
export const getBatchTimesheetStatus = async (personnelIds, year, month) => {
  try {
    const response = await api.post('/timesheet/status/batch', personnelIds, {
      params: { year, month }
    });
    return response.data.statuses;
  } catch (error) {
    console.error('Error fetching batch timesheet status:', error);
    throw error;
  }
};

/**
 * Get aggregated timesheet summary for a period
 * 
 * Returns breakdown by timesheet code (W, T, M, etc.) with totals.
 * Useful for preview before locking and payslip generation.
 * 
 * @param {number} personnelId - Personnel ID
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} Summary with breakdown by code
 */
export const getTimesheetPeriodSummary = async (personnelId, year, month) => {
  try {
    const response = await api.get('/timesheet/period-summary', {
      params: { personnel_id: personnelId, year, month }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching timesheet summary:', error);
    throw error;
  }
};

// =============================================================================
// TIME ENTRY RANGES (Phase C - Range-based Time Entries)
// =============================================================================

/**
 * Create a new time entry range
 * 
 * Creates a range-based time entry with manual code (M, T, P, O, V, H).
 * Server validates no overlap with existing entries.
 * 
 * @param {Object} data - Entry data
 * @param {number} data.personnel_id - Personnel ID
 * @param {string} data.start_date - Start date (YYYY-MM-DD)
 * @param {string} data.end_date - End date (YYYY-MM-DD)
 * @param {string} data.code - Timesheet code (M, T, P, O, V, H - not W)
 * @param {string} [data.notes] - Optional notes
 * @returns {Promise<Object>} Created entry with id
 */
export const createTimeEntryRange = async (data) => {
  try {
    const response = await api.post('/timesheet/entry-range', data);
    return response.data;
  } catch (error) {
    console.error('Error creating time entry range:', error);
    throw error;
  }
};

/**
 * Update existing time entry range
 * 
 * @param {number} entryId - Entry ID
 * @param {Object} data - Updated fields
 * @param {string} [data.start_date] - Start date
 * @param {string} [data.end_date] - End date
 * @param {string} [data.code] - Timesheet code
 * @param {string} [data.notes] - Notes
 * @returns {Promise<Object>} Updated entry
 */
export const updateTimeEntryRange = async (entryId, data) => {
  try {
    const response = await api.put(`/timesheet/entry-range/${entryId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating time entry range:', error);
    throw error;
  }
};

/**
 * Delete time entry range
 * 
 * @param {number} entryId - Entry ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteTimeEntryRange = async (entryId) => {
  try {
    const response = await api.delete(`/timesheet/entry-range/${entryId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting time entry range:', error);
    throw error;
  }
};

/**
 * Get ALL time entry ranges for the company in a specific month.
 * Returns a flat list with personnel info.
 *
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} { year, month, total_entries, entries: [...] }
 */
export const getAllEntryRanges = async (year, month) => {
  try {
    const response = await api.get('/timesheet/entry-ranges/all', {
      params: { year, month }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all entry ranges:', error);
    throw error;
  }
};

/**
 * Get time entry ranges for personnel in a month
 * 
 * @param {number} personnelId - Personnel ID
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Array>} List of entry ranges
 */
export const getTimeEntryRanges = async (personnelId, year, month) => {
  try {
    const response = await api.get('/timesheet/entry-ranges', {
      params: { personnel_id: personnelId, year, month }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching time entry ranges:', error);
    throw error;
  }
};

/**
 * Get complete calendar for personnel (W auto + manual entries)
 * 
 * Returns calendar with W (work) auto-generated for active days
 * and manual entries (M, T, P, O, V, H) overlaid.
 * 
 * @param {number} personnelId - Personnel ID
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} Calendar data with summary
 */
export const getPersonnelCalendar = async (personnelId, year, month) => {
  try {
    const response = await api.get(`/timesheet/calendar/${personnelId}`, {
      params: { year, month }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching personnel calendar:', error);
    throw error;
  }
};

/**
 * Get calendars for multiple personnel (bulk)
 * 
 * Useful for company timesheet view showing all personnel.
 * 
 * @param {Array<number>} personnelIds - Array of personnel IDs
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @returns {Promise<Object>} Calendars keyed by personnel ID
 */
export const getBulkCalendars = async (personnelIds, year, month) => {
  try {
    const response = await api.get('/timesheet/calendars/bulk', {
      params: { 
        year, 
        month,
        personnel_ids: personnelIds.join(',')
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching bulk calendars:', error);
    throw error;
  }
};

/**
 * Validate calendar before locking
 * 
 * Pre-validates that all personnel have calendars ready for lock.
 * 
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {number} [projectId] - Optional project filter
 * @param {number} [departmentId] - Optional department filter
 * @returns {Promise<Object>} Validation result with warnings
 */
export const validateCalendar = async (year, month, projectId = null, departmentId = null) => {
  try {
    const response = await api.post('/timesheet/validate-calendar', null, {
      params: { 
        year, 
        month,
        ...(projectId && { project_id: projectId }),
        ...(departmentId && { department_id: departmentId })
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating calendar:', error);
    throw error;
  }
};


// =============================================================================
// ETAPA 5a — EL TAREO DE UN MES: pestanas generadas y filas por TRAMO
// =============================================================================

/**
 * Las pestanas del mes: la empresa, mas un proyecto por cada uno CON GENTE.
 *
 * Nunca dos fijas. Con un proyecto se veran como las dos de siempre; con tres,
 * funciona sin tocar codigo. Fijarlas devolveria a la interfaz el interruptor
 * binario que la etapa 4a acaba de quitar de la base.
 */
export const getTimesheetMonthTabs = async (year, month) => {
  const response = await api.get('/timesheet/month/tabs', { params: { year, month } });
  return response.data;
};

/**
 * Una fila por PERSONA Y TRAMO, con sus dias dentro del mes.
 *
 * Mas de una fila por persona es lo normal: quien se traslada el dia 15 sale en
 * las dos pestanas con los dias de cada una. Sale quien tuvo al menos un dia,
 * liquidados incluidos.
 */
export const getTimesheetMonthRoster = async (year, month) => {
  const response = await api.get('/timesheet/month/roster', { params: { year, month } });
  return response.data;
};

/**
 * Las entradas del mes ENTERO, agrupadas `{personnel_id: {fecha: entrada}}`.
 *
 * Sin `project_id`: la rejilla pinta todas las pestanas del mes y una persona
 * puede tener dias en dos. Filtrar por proyecto aqui perderia justo los dias
 * del otro lado de un traslado.
 */
export const getTimesheetMonthEntries = async (year, month) => {
  const response = await api.get('/timesheet/entries/by-month', {
    params: { year, month },
  });
  return response.data;
};

/**
 * Los SIETE codigos de la rejilla, con su color.
 *
 * Vienen del backend a proposito: una segunda lista escrita aqui se quedaria
 * vieja el dia que la de alla cambie, y nadie lo notaria hasta que alguien no
 * cobrara un dia.
 */
export const getTimesheetMonthCodes = async () => {
  const response = await api.get('/timesheet/month/codes');
  return response.data;
};

/**
 * Pone (o quita, con `code = null`) el codigo de UN dia.
 *
 * Ruta propia y no `POST /timesheet/entries`: aquella rechaza a quien no este
 * ACTIVE --y la rejilla incluye a los liquidados--, marca el dia con el centro
 * de coste de HOY en vez del del dia, y da 400 si el dia ya tenia entrada.
 */
export const setTimesheetMonthDay = async ({ personnelId, entryDate, code }) => {
  const response = await api.put('/timesheet/month/day', {
    personnel_id: personnelId,
    entry_date: entryDate,
    code,
  });
  return response.data;
};
